import type { GeneralParameters } from '../../general/parameters/general'
import type { PlaybackSnapshot } from '../models/play-start-stop'
import type { PlayStopParameters } from '../parameters/play-stop'

import { Analytix } from '~/shared/analytix'
import { AnalytixEvent } from '../../general/event'
import { Gate, seeds } from '../../general/gate'
import { PlayStopReason } from '../index.h'
import { parameters } from '../parameters/play-stop'

const LIVE_CONTENT_TYPES = ['tv', 'event']

/**
 * Queue event wrapper for `play_stop`.
 *
 * Closes play gate and sends final playback snapshot metrics that can still be
 * resolved after stream unload or player disposal.
 */

export class PlayStopEvent extends AnalytixEvent<PlayStopParameters> {
  name = 'play_stop'
  group = 'player'
  parameters = parameters
  sender = Analytix.prototype.sendPlayStopEvent

  // `play_stop` event uses all active player/session gates and closes play gate
  gates = [
    Gate.Uses(seeds.session),
    Gate.Uses(seeds.player),
    Gate.Closes(seeds.play),
  ]

  // `play_stop` event should flush stale play events and keep latest stop state
  override flushing = ['play_start']

  // do not expire
  override timeout = Infinity

  public reason: PlayStopReason
  private snapshot?: PlaybackSnapshot

  constructor(
    reason: PlayStopReason = PlayStopReason.userPause,
    snapshot?: PlaybackSnapshot
  ) {
    super()

    this.reason = reason
    this.snapshot = snapshot
    this.verify()
  }

  validate(): false | [GeneralParameters, PlayStopParameters] {
    const parameters: PlayStopParameters = Object.fromEntries(this.parameters)
    parameters.contentStopReasons = this.reason
    parameters.stopSecond = this.snapshot?.currentTime || parameters.stopSecond
    parameters.contentLength =
      this.snapshot?.contentLength || parameters.contentLength
    parameters.audioTrackLanguage =
      this.snapshot?.audioTrackLanguage || parameters.audioTrackLanguage
    parameters.subtitlesLanguage =
      this.snapshot?.subtitlesLanguage || parameters.subtitlesLanguage
    parameters.videoQuality =
      this.snapshot?.videoQuality || parameters.videoQuality
    parameters.playbackSessionId =
      this.snapshot?.playbackSessionId || parameters.playbackSessionId

    const isLive = LIVE_CONTENT_TYPES.includes(
      String(this.parameters.get('contentType') ?? '')
    )
    if (isLive) {
      parameters.stopSecond =
        parameters.totalWatchedTimeFromLatestPlayStart ?? '0'
    }

    // valid conditions for `play_stop` event:
    // - `contentUrl` parameter is available, which means content metadata is known
    const valid = this.parameters.get('contentUrl') != null

    return valid && [Object.fromEntries(this.general), parameters]
  }
}
